/**
 * ModeratorChat — Full-screen overlay for post-session moderator consultation
 * Purple theme, simplified from PersonaDeepDive (no belief tracking, no tabs)
 */
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Send, Loader2, MessageCircle, AlertTriangle, Sparkles
} from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';

export function ModeratorChat({ moderatorChat, sessionId, moderatorSummary, config }) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [moderatorChat.chatMessages, moderatorChat.streamingContent]);

  useEffect(() => {
    if (!moderatorChat.isStreaming && inputRef.current) {
      inputRef.current.focus();
    }
  }, [moderatorChat.isStreaming]);

  const handleSend = (e) => {
    e.preventDefault();
    if (!inputValue.trim() || moderatorChat.isStreaming) return;
    moderatorChat.sendMessage(sessionId, inputValue);
    setInputValue('');
  };

  const capabilities = [
    t('moderatorChat.capability1'),
    t('moderatorChat.capability2'),
    t('moderatorChat.capability3'),
    t('moderatorChat.capability4'),
  ];

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', damping: 25, stiffness: 200 }}
      className="fixed inset-0 z-50 bg-stone-50 overflow-hidden flex flex-col"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900 to-purple-800 px-6 py-4 flex items-center gap-4 flex-shrink-0">
        <button
          onClick={moderatorChat.closeChat}
          className="flex items-center gap-2 text-purple-200 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-medium">{t('moderatorChat.backToReport')}</span>
        </button>
        <div className="flex-1 text-center">
          <h1 className="font-serif text-xl text-white">
            {t('moderatorChat.title')}
          </h1>
          <p className="text-sm text-purple-300">{config?.topic}</p>
        </div>
        <div className="w-24" />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel — Moderator Summary + Capabilities */}
        <div className="w-80 lg:w-96 border-r border-stone-200 bg-white overflow-y-auto flex-shrink-0">
          <div className="p-6 space-y-6">
            {/* Summary */}
            <div>
              <h3 className="font-serif text-lg text-stone-900 mb-3 flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-purple-600" />
                {t('moderatorChat.summaryTitle')}
              </h3>
              {moderatorSummary ? (
                <p className="text-sm text-stone-600 leading-relaxed whitespace-pre-wrap">
                  {moderatorSummary}
                </p>
              ) : (
                <p className="text-sm text-stone-400 italic">{t('moderatorChat.noSummary')}</p>
              )}
            </div>

            {/* Capabilities */}
            <div>
              <h3 className="font-serif text-lg text-stone-900 mb-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                {t('moderatorChat.capabilitiesTitle')}
              </h3>
              <ul className="space-y-2">
                {capabilities.map((cap, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-stone-600">
                    <span className="flex-shrink-0 w-5 h-5 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-xs font-bold mt-0.5">
                      {idx + 1}
                    </span>
                    {cap}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Right Panel — Chat */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {moderatorChat.chatMessages.length === 0 && !moderatorChat.isStreaming && (
              <div className="text-center py-12 text-stone-400">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 text-purple-300" />
                <p className="text-lg">{t('moderatorChat.emptyState')}</p>
                <p className="text-sm mt-1">{t('moderatorChat.emptyStateHint')}</p>
              </div>
            )}
            {moderatorChat.chatMessages.map((msg, idx) => (
              <ChatMessage key={idx} message={msg} t={t} />
            ))}
            {moderatorChat.isStreaming && moderatorChat.streamingContent && (
              <ChatMessage
                message={{ role: 'moderator', content: moderatorChat.streamingContent }}
                t={t}
                isStreaming
              />
            )}
            {moderatorChat.isStreaming && !moderatorChat.streamingContent && (
              <div className="flex items-center gap-2 text-purple-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">{t('moderatorChat.thinking')}</span>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-stone-200 bg-white p-4">
            <form onSubmit={handleSend} className="flex gap-3">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={t('moderatorChat.inputPlaceholder')}
                className="flex-1 px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                disabled={moderatorChat.isStreaming}
              />
              <button
                type="submit"
                disabled={!inputValue.trim() || moderatorChat.isStreaming}
                className="px-4 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="w-5 h-5" />
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Error banner */}
      <AnimatePresence>
        {moderatorChat.error && (
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-red-100 text-red-700 px-6 py-3 rounded-xl shadow-lg flex items-center gap-2"
          >
            <AlertTriangle className="w-4 h-4" />
            {moderatorChat.error}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}


function ChatMessage({ message, t, isStreaming }) {
  const isUser = message.role === 'user';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] px-4 py-3 rounded-2xl ${
          isUser
            ? 'bg-purple-600 text-white rounded-br-sm'
            : 'bg-white border border-purple-200 text-stone-800 rounded-bl-sm shadow-sm'
        }`}
      >
        {!isUser && (
          <div className="text-xs font-medium text-purple-600 mb-1">{t('moderatorChat.moderatorLabel')}</div>
        )}
        <div className="text-sm leading-relaxed whitespace-pre-wrap">
          {message.content}
          {isStreaming && (
            <span className="inline-block w-1.5 h-4 bg-purple-400 ml-0.5 animate-pulse rounded" />
          )}
        </div>
      </div>
    </div>
  );
}
